// /charges/:id — detalhe de cobrança com payments + timeline (Story 4.10 AC3).

import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  CalendarClock,
  Check,
  Download,
  ExternalLink,
  FileText,
  Hash,
  Paperclip,
  RefreshCw,
  RotateCcw,
  ShieldX,
  User,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  formatBR,
  formatBRDateTime,
  formatRelativeDate,
  parseLocalDate,
} from '@control-center/shared';
import type { ChargeListItem, ChargeTimelineEntry } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { MarkPaidManualModal } from '../../components/charges/MarkPaidManualModal';
import { UpdateDueDateModal } from '../../components/charges/UpdateDueDateModal';
import { CancelFiscalInvoiceModal } from '../../components/fiscal/CancelFiscalInvoiceModal';
import { ManualEmitModal } from '../../components/fiscal/ManualEmitModal';
import {
  CHARGE_STATUS_DOT,
  CHARGE_STATUS_LABEL,
  chargeTypeLabel,
  canMarkPaidManual,
  canRefund,
  canRetry,
  canUpdateDueDate,
  formatAmountBR,
  isInChargebackDispute,
} from '../../components/charges/charge-display';
import { PillButton } from '../../components/clients/PillButton';
import { RefundModal } from '../../components/refunds/RefundModal';
import {
  useAcceptChargebackLoss,
  useChargeDetail,
  useRetryFailedCharge,
} from '../../hooks/use-charges';
import { useClient } from '../../hooks/use-clients';
import {
  triggerFiscalDownload,
  useCanCancelFiscalInvoice,
  useFiscalInvoiceByCharge,
} from '../../hooks/use-fiscal';
import { extractErrorMessage } from '../../lib/errors';

const ACTION_LABEL: Record<string, string> = {
  create: 'Criada',
  update: 'Atualizada',
  delete: 'Removida',
  state_change: 'Mudança de estado',
  custom: 'Ação',
};

const SOURCE_LABEL: Record<string, string> = {
  manual_avulsa: 'Cobrança avulsa criada',
  mark_paid_manual: 'Marcada paga manualmente',
  update_due_date: 'Vencimento atualizado',
  retry_failed_charge: 'Tentativa de reprocessamento',
  refund_manual: 'Reembolso manual',
  accept_chargeback_loss: 'Perda de chargeback aceita',
};

function formatTimelineLabel(entry: ChargeTimelineEntry): string {
  if (entry.source) {
    const sourceLabel = SOURCE_LABEL[entry.source];
    if (sourceLabel) return sourceLabel;
  }
  return ACTION_LABEL[entry.action] ?? entry.action;
}

export function ChargeDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQ = useChargeDetail(id);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [updateDueOpen, setUpdateDueOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [manualEmitOpen, setManualEmitOpen] = useState(false);
  const [cancelFiscalOpen, setCancelFiscalOpen] = useState(false);
  const retryMutation = useRetryFailedCharge();
  const [retrying, setRetrying] = useState(false);
  const acceptLossMutation = useAcceptChargebackLoss();
  const [acceptingLoss, setAcceptingLoss] = useState(false);

  // Fiscal — Story 10.4/10.5/10.7
  const fiscalInvQ = useFiscalInvoiceByCharge(id);
  const clientQ = useClient(detailQ.data?.clientId);
  const fiscalInv = fiscalInvQ.data;
  const canCancelQ = useCanCancelFiscalInvoice(fiscalInv?.id, fiscalInv?.status === 'issued');

  if (detailQ.isPending) {
    return (
      <div className="px-6 py-12 text-center text-[13px] text-muted">Carregando cobrança…</div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="px-6 py-12">
        <div className="rounded-[10px] border border-bad/30 bg-bad/5 p-6 text-[13px] text-bad">
          Cobrança não encontrada.
        </div>
        <button
          type="button"
          onClick={() => navigate('/charges')}
          className="mt-4 text-[12px] text-muted hover:text-ink"
        >
          <ArrowLeft size={12} className="inline" /> Voltar pra lista
        </button>
      </div>
    );
  }

  const charge = detailQ.data;

  async function handleRetry(): Promise<void> {
    if (!charge) return;
    setRetrying(true);
    try {
      const result = await retryMutation.mutateAsync(charge.id);
      toast.success(`Reprocessada (tentativa ${result.attemptsCount})`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao reprocessar'));
    } finally {
      setRetrying(false);
    }
  }

  async function handleAcceptLoss(): Promise<void> {
    if (!charge) return;
    const confirmed = window.confirm(
      'Confirma aceitar a perda do chargeback?\n\n' +
        'A cobrança ficará estornada e a assinatura vinculada (se houver) será cancelada. ' +
        'Esta ação não chama o Asaas — eles processam automaticamente no fim do prazo de contestação.',
    );
    if (!confirmed) return;
    setAcceptingLoss(true);
    try {
      const result = await acceptLossMutation.mutateAsync(charge.id);
      toast.success('Perda aceita.', {
        description: result.subscriptionCancelled
          ? 'Cobrança estornada e assinatura cancelada.'
          : 'Cobrança estornada.',
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao aceitar perda'));
    } finally {
      setAcceptingLoss(false);
    }
  }

  // Charge pra modais (shape esperado)
  const chargeForModals: Pick<
    ChargeListItem,
    'id' | 'clientName' | 'description' | 'amount' | 'dueDate'
  > = {
    id: charge.id,
    clientName: charge.clientName,
    description: charge.description,
    amount: charge.amount,
    dueDate: charge.dueDate,
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      {/* Voltar */}
      <button
        type="button"
        onClick={() => navigate('/charges')}
        className="mb-4 flex items-center gap-1.5 text-[12px] text-muted hover:text-ink"
      >
        <ArrowLeft size={14} strokeWidth={1.8} />
        Voltar pra cobranças
      </button>

      {/* Story 4.12 — Banner "EM DISPUTA" sempre visível antes do header quando aplicável */}
      {isInChargebackDispute(charge.status) && (
        <div className="mb-4 flex items-start gap-3 rounded-[12px] border border-bad/40 bg-bad/5 p-4">
          <ShieldX size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-bad" />
          <div className="flex-1">
            <div className="text-[14px] font-semibold text-bad">EM DISPUTA</div>
            <p className="mt-1 text-[12px] leading-[1.5] text-ink-2">
              O Asaas reportou chargeback nessa cobrança. A assinatura vinculada está suspensa.
              Contestar pelo painel do Asaas (botão abaixo) ou aceitar a perda — sem decisão, o
              Asaas finaliza automaticamente no fim do prazo.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  'inline-block h-[10px] w-[10px] rounded-full ring-2',
                  CHARGE_STATUS_DOT[charge.status] ?? 'bg-muted-2 ring-muted-3/15',
                )}
              />
              <span className="text-[12px] uppercase tracking-wider text-muted">
                {CHARGE_STATUS_LABEL[charge.status] ?? charge.status}
              </span>
              <span className="text-muted-3">·</span>
              <span className="text-[12px] text-muted">{chargeTypeLabel(charge)}</span>
            </div>
            <h1 className="text-[22px] font-semibold tracking-[-0.005em]">{charge.description}</h1>
            <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted">
              <User size={12} strokeWidth={1.8} />
              {charge.clientName}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[28px] font-semibold tnum">{formatAmountBR(charge.amount)}</div>
            {charge.effectivePrice && charge.effectivePrice !== charge.amount && (
              <div className="mt-1 text-[12px] text-muted line-through tnum">
                {formatAmountBR(charge.effectivePrice)}
              </div>
            )}
          </div>
        </div>

        {/* Quick info */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-[10px] border border-line bg-panel p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-3">Vencimento</div>
            <div className="mt-1 text-[13px] text-ink tnum">
              {formatBR(parseLocalDate(charge.dueDate))}
            </div>
            <div className="mt-0.5 text-[11px] text-muted">
              {formatRelativeDate(charge.dueDate)}
            </div>
          </div>
          <div className="rounded-[10px] border border-line bg-panel p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-3">Competência</div>
            <div className="mt-1 text-[13px] text-ink tnum">{charge.competence}</div>
          </div>
          <div className="rounded-[10px] border border-line bg-panel p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-3">Tentativas</div>
            <div className="mt-1 text-[13px] text-ink tnum">{charge.attemptsCount}</div>
          </div>
          <div className="rounded-[10px] border border-line bg-panel p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-3">Método</div>
            <div className="mt-1 text-[13px] text-ink">{charge.paymentMethod ?? '—'}</div>
          </div>
        </div>

        {/* Ações por status (AC4) */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {charge.paymentLink && (
            <a
              href={charge.paymentLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-[8px] border border-line px-3 py-1.5 text-[12px] text-ink-2 hover:border-line-2 hover:text-ink"
            >
              <ExternalLink size={12} strokeWidth={1.8} />
              Link de pagamento
            </a>
          )}
          {canRetry(charge.status) && (
            <PillButton onClick={handleRetry} disabled={retrying}>
              <RefreshCw
                size={12}
                strokeWidth={1.8}
                className={retrying ? 'animate-spin' : undefined}
              />
              {retrying ? 'Reprocessando…' : 'Reprocessar'}
            </PillButton>
          )}
          {canUpdateDueDate(charge.status) && (
            <PillButton onClick={() => setUpdateDueOpen(true)}>
              <CalendarClock size={12} strokeWidth={1.8} />
              Adiar vencimento
            </PillButton>
          )}
          {canMarkPaidManual(charge.status) && (
            <PillButton onClick={() => setMarkPaidOpen(true)} solid>
              <Check size={12} strokeWidth={1.8} />
              Marcar paga manualmente
            </PillButton>
          )}
          {canRefund(charge.status) && Number(charge.remainingBalance) > 0 && (
            <PillButton onClick={() => setRefundOpen(true)}>
              <RotateCcw size={12} strokeWidth={1.8} />
              Registrar reembolso
            </PillButton>
          )}
          {/* Story 4.12 — ações de chargeback em disputa */}
          {isInChargebackDispute(charge.status) && (
            <>
              {charge.paymentLink && (
                <a
                  href={charge.paymentLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-[8px] border border-line px-3 py-1.5 text-[12px] text-ink-2 hover:border-line-2 hover:text-ink"
                >
                  <ExternalLink size={12} strokeWidth={1.8} />
                  Contestar no Asaas
                </a>
              )}
              <PillButton onClick={handleAcceptLoss} disabled={acceptingLoss}>
                <AlertTriangle size={12} strokeWidth={1.8} />
                {acceptingLoss ? 'Processando…' : 'Aceitar perda'}
              </PillButton>
            </>
          )}

          {/* Story 10.4 — Emitir NF manual (charge paga incluindo manual + sem NF ativa) */}
          {(['paga', 'paga_manual'] as readonly string[]).includes(charge.status) &&
            (!fiscalInv ||
              ['failed', 'pending_data', 'cancelled', 'discarded'].includes(fiscalInv.status)) && (
              <PillButton onClick={() => setManualEmitOpen(true)}>
                <FileText size={12} strokeWidth={1.8} />
                Emitir NF manual
              </PillButton>
            )}

          {/* Story 10.5 — Cancelar NF (status issued + janela legal aberta) */}
          {fiscalInv?.status === 'issued' && (
            <PillButton
              danger
              onClick={() => setCancelFiscalOpen(true)}
              disabled={canCancelQ.data?.canCancel === false}
              title={
                canCancelQ.data?.canCancel === false
                  ? (canCancelQ.data?.reason ?? 'Cancelamento não disponível')
                  : 'Cancelar NF emitida'
              }
            >
              <XCircle size={12} strokeWidth={1.8} />
              Cancelar NF
            </PillButton>
          )}
        </div>

        {/* Lastro de NF avulsa — cobrança fantasma criada pela emissão standalone */}
        {charge.type === 'outro' && charge.paymentMethod === 'external' && (
          <div className="mt-4 rounded-[10px] border border-line/60 bg-panel-2 p-3 text-[12px] leading-relaxed text-muted">
            <strong className="text-ink-2">Registro contábil de NF avulsa</strong> — esta cobrança
            foi criada automaticamente pra lastrear um recebimento feito fora do Asaas (Pix direto,
            dinheiro, transferência). Não é uma cobrança enviada ao cliente.
            {!fiscalInv ? (
              <span className="mt-1 block font-medium text-warn">
                A NF deste recebimento ainda NÃO foi emitida. Resolva a pendência e use o botão
                “Emitir NF manual” acima.
              </span>
            ) : ['pending_data', 'failed'].includes(fiscalInv.status) ? (
              <span className="mt-1 block font-medium text-warn">
                A emissão da NF falhou: {fiscalInv.errorMessage ?? 'erro não informado'} Depois de
                corrigir, use “Emitir NF manual” acima.
              </span>
            ) : fiscalInv.status === 'issued' ? (
              <span className="mt-1 block text-good">
                NF emitida — detalhes no card Nota Fiscal abaixo.
              </span>
            ) : null}
          </div>
        )}

        {/* Error message (failed) */}
        {charge.status === 'failed' && charge.errorMessage && (
          <div className="mt-4 rounded-[10px] border border-bad/30 bg-bad/5 p-3 text-[12px] text-bad">
            <strong>Último erro:</strong> {charge.errorMessage}
          </div>
        )}
      </header>

      {/* Pagamentos */}
      <section className="mb-6">
        <h2 className="mb-2 text-[14px] font-medium uppercase tracking-wider text-muted-3">
          Pagamentos
        </h2>
        {charge.payments.length === 0 ? (
          <div className="rounded-[10px] border border-line bg-panel p-6 text-center text-[13px] text-muted">
            <Banknote size={18} strokeWidth={1.5} className="mx-auto mb-2 text-muted-3" />
            Nenhum pagamento registrado ainda.
          </div>
        ) : (
          <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line bg-panel-2 text-[11px] uppercase tracking-wider text-muted-3">
                <tr>
                  <th className="px-4 py-2 text-left">Quando</th>
                  <th className="px-4 py-2 text-left">Método</th>
                  <th className="px-4 py-2 text-right">Valor</th>
                  <th className="px-4 py-2 text-left">Referência</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {charge.payments.map((p) => (
                  <tr key={p.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2 text-muted tnum">
                      {p.paidAt ? formatBRDateTime(new Date(p.paidAt)) : '—'}
                    </td>
                    <td className="px-4 py-2 text-muted">{p.method ?? '—'}</td>
                    <td className="px-4 py-2 text-right tnum text-ink">
                      {formatAmountBR(p.amount)}
                    </td>
                    <td className="px-4 py-2 font-mono text-[11px] text-muted">
                      {p.externalReference ?? p.asaasPaymentId ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-muted">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Reembolsos (Story 4.11) */}
      <section className="mb-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-[14px] font-medium uppercase tracking-wider text-muted-3">
            Reembolsos
          </h2>
          {charge.refunds.length > 0 && (
            <div className="text-[11px] text-muted tnum">
              Reembolsado: <span className="text-ink">{formatAmountBR(charge.refundedTotal)}</span>
              {' · '}
              Saldo: <span className="text-ink">{formatAmountBR(charge.remainingBalance)}</span>
            </div>
          )}
        </div>
        {charge.refunds.length === 0 ? (
          <div className="rounded-[10px] border border-line bg-panel p-6 text-center text-[13px] text-muted">
            Nenhum reembolso registrado.
          </div>
        ) : (
          <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line bg-panel-2 text-[11px] uppercase tracking-wider text-muted-3">
                <tr>
                  <th className="px-4 py-2 text-left">Quando</th>
                  <th className="px-4 py-2 text-right">Valor</th>
                  <th className="px-4 py-2 text-left">Motivo</th>
                  <th className="px-4 py-2 text-left">Referência</th>
                  <th className="px-4 py-2 text-left">Operador</th>
                </tr>
              </thead>
              <tbody>
                {charge.refunds.map((r) => (
                  <tr key={r.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2 text-muted tnum">
                      {formatBRDateTime(new Date(r.refundedAt))}
                    </td>
                    <td className="px-4 py-2 text-right tnum text-ink">
                      {formatAmountBR(r.amount)}
                    </td>
                    <td className="px-4 py-2 text-muted">{r.reason ?? '—'}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-muted">
                      <div className="flex items-center gap-1">
                        {r.externalReference ?? '—'}
                        {r.attachmentUrl && (
                          <a
                            href={r.attachmentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-muted-3 hover:text-ink"
                            title="Ver anexo"
                          >
                            <Paperclip size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted">{r.byUserName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Story 10.7 — Nota Fiscal (se já emitida) */}
      {fiscalInv && (
        <section className="mb-6">
          <h2 className="mb-2 text-[14px] font-medium uppercase tracking-wider text-muted-3">
            Nota Fiscal
          </h2>
          <FiscalInvoiceCard
            fiscalInv={fiscalInv}
            onDownloadPdf={() =>
              void triggerFiscalDownload(
                fiscalInv.id,
                'pdf',
                `NF-${fiscalInv.invoiceNumber ?? fiscalInv.id}.pdf`,
              )
            }
            onDownloadXml={() =>
              void triggerFiscalDownload(
                fiscalInv.id,
                'xml',
                `NF-${fiscalInv.invoiceNumber ?? fiscalInv.id}.xml`,
              )
            }
          />
        </section>
      )}

      {/* Timeline */}
      <section>
        <h2 className="mb-2 text-[14px] font-medium uppercase tracking-wider text-muted-3">
          Histórico
        </h2>
        {charge.timeline.length === 0 ? (
          <div className="rounded-[10px] border border-line bg-panel p-6 text-center text-[13px] text-muted">
            Sem entradas de histórico.
          </div>
        ) : (
          <ol className="space-y-2">
            {charge.timeline.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-3 rounded-[10px] border border-line bg-panel p-3"
              >
                <Hash size={14} strokeWidth={1.8} className="mt-0.5 shrink-0 text-muted-3" />
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium text-ink">
                      {formatTimelineLabel(entry)}
                    </span>
                    <span className="text-[11px] text-muted tnum">
                      {formatBRDateTime(new Date(entry.createdAt))}
                    </span>
                  </div>
                  {entry.userName && (
                    <div className="mt-0.5 text-[11px] text-muted">por {entry.userName}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <MarkPaidManualModal
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        charge={chargeForModals}
      />
      <UpdateDueDateModal
        open={updateDueOpen}
        onOpenChange={setUpdateDueOpen}
        charge={chargeForModals}
      />
      <RefundModal
        open={refundOpen}
        onOpenChange={setRefundOpen}
        charge={{
          id: charge.id,
          clientName: charge.clientName,
          description: charge.description,
        }}
        remainingBalance={charge.remainingBalance}
      />

      <ManualEmitModal
        open={manualEmitOpen}
        onOpenChange={setManualEmitOpen}
        chargeId={charge.id}
        chargeAmount={charge.amount}
        defaultTakerDocument={
          clientQ.data?.fiscalDocumentOverride ?? clientQ.data?.document ?? null
        }
        clientPrincipalDocument={clientQ.data?.document ?? null}
        defaultServiceDescription={null}
      />

      {fiscalInv && (
        <CancelFiscalInvoiceModal
          open={cancelFiscalOpen}
          onOpenChange={setCancelFiscalOpen}
          fiscalInvoiceId={fiscalInv.id}
          invoiceNumber={fiscalInv.invoiceNumber}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FiscalInvoiceCard — exibição compacta da NF na página de detalhe da charge
// (Story 10.7 AC7 simplificado — drawer completo na tela /fiscal/invoices)
// ---------------------------------------------------------------------------

import type { FiscalInvoice } from '../../lib/fiscal-api';

const FISCAL_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  issuing: 'Em emissão',
  issued: 'Emitida',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  pending_data: 'Dados pendentes',
  discarded: 'Descartada',
};

const FISCAL_STATUS_TONE: Record<string, string> = {
  pending: 'border-info/40 bg-info/10 text-info',
  issuing: 'border-info/40 bg-info/10 text-info',
  issued: 'border-good/40 bg-good/10 text-good',
  failed: 'border-bad/40 bg-bad/10 text-bad',
  cancelled: 'border-line bg-panel-3 text-muted',
  pending_data: 'border-warn/40 bg-warn/10 text-warn',
  discarded: 'border-line bg-panel-3 text-muted-2',
};

function FiscalInvoiceCard({
  fiscalInv,
  onDownloadPdf,
  onDownloadXml,
}: {
  fiscalInv: FiscalInvoice;
  onDownloadPdf: () => void;
  onDownloadXml: () => void;
}): JSX.Element {
  const tone = FISCAL_STATUS_TONE[fiscalInv.status] ?? 'border-line bg-panel-2 text-muted';
  return (
    <div className="rounded-[10px] border border-line bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileText size={14} strokeWidth={1.8} className="text-muted" />
            <span className="text-[13px] font-medium text-ink">
              {fiscalInv.invoiceNumber ?? 'NF sem número'}
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider',
                tone,
              )}
            >
              {FISCAL_STATUS_LABEL[fiscalInv.status] ?? fiscalInv.status}
            </span>
          </div>
          {fiscalInv.takerDocument && (
            <p className="mt-1 text-[11px] text-muted tnum">
              Tomador: {fiscalInv.takerDocument}
              {fiscalInv.takerDocumentWasOverride && (
                <span className="ml-1 text-muted-2">(override)</span>
              )}
            </p>
          )}
          {fiscalInv.errorMessage && (
            <p className="mt-2 text-[11px] text-bad">{fiscalInv.errorMessage}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {fiscalInv.externalId && (
            <>
              <button
                type="button"
                onClick={onDownloadPdf}
                className="inline-flex items-center gap-1 rounded-[8px] border border-line px-3 py-1.5 text-[12px] text-ink-2 hover:border-line-2 hover:text-ink"
              >
                <Download size={12} strokeWidth={1.8} />
                PDF
              </button>
              <button
                type="button"
                onClick={onDownloadXml}
                className="inline-flex items-center gap-1 rounded-[8px] border border-line px-3 py-1.5 text-[12px] text-ink-2 hover:border-line-2 hover:text-ink"
              >
                <Download size={12} strokeWidth={1.8} />
                XML
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
